'use client';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  Activity,
  BarChart3,
  Boxes,
  Briefcase,
  Compass,
  CornerDownLeft,
  Database,
  GitMerge,
  Globe,
  HeartPulse,
  Layers,
  Moon,
  Network,
  Palette,
  Receipt,
  Rows3,
  Search,
  Shield,
  Sun,
  TrendingUp,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '@/lib/util/cn';
import type { Strategy } from '@/types/api';
import type { Client } from '@/types/domain';

/**
 * Command palette — the keyboard-first surface promised in the spec.
 *
 *   • ⌘K (or Ctrl+K) toggles. Esc closes.
 *   • Groups: Navigate / Theme & density / Live entities (strategies,
 *     clients) / Actions.
 *   • Live entities pull from the existing TanStack Query cache — no
 *     extra fetches; if the cache has nothing yet, that group just hides.
 *   • Selecting a route uses next/router, selecting an entity navigates
 *     to its detail page.
 *   • cmdk handles the fuzzy search + keyboard navigation; we just
 *     decorate.
 */

type NavId = string;

interface NavRow {
  id: NavId;
  label: string;
  hint?: string;
  icon: LucideIcon;
  mnemonic?: string;
  run: () => void;
}

interface NavGroup {
  heading: string;
  rows: NavRow[];
}

const ROUTE_ROWS: ReadonlyArray<{
  href: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  mnemonic: string;
}> = [
  { href: '/trades', label: 'Trades', hint: 'trade', icon: Receipt, mnemonic: 'g t' },
  { href: '/pnl', label: 'P&L', hint: 'trade', icon: BarChart3, mnemonic: 'g p' },
  { href: '/risk', label: 'Risk Rules', hint: 'risk & controls', icon: Shield, mnemonic: 'g r' },
  { href: '/strategies', label: 'Strategies', hint: 'strategy fleet', icon: TrendingUp, mnemonic: 'g s' },
  { href: '/subscriptions', label: 'Subscriptions', hint: 'strategy fleet', icon: GitMerge, mnemonic: 'g u' },
  { href: '/broker-accounts', label: 'Broker Accounts', hint: 'accounts', icon: Wallet, mnemonic: 'g n' },
  { href: '/brokers', label: 'Brokers', hint: 'accounts', icon: Briefcase, mnemonic: 'g b' },
  { href: '/broker-drivers', label: 'Broker Drivers', hint: 'accounts', icon: Network, mnemonic: 'g d' },
  { href: '/clients', label: 'Clients', hint: 'accounts', icon: Users, mnemonic: 'g c' },
  { href: '/watchlists', label: 'Watchlists', hint: 'market data', icon: Activity, mnemonic: 'g l' },
  { href: '/option-chains', label: 'Option Chains', hint: 'market data', icon: Layers, mnemonic: 'g y' },
  { href: '/instruments', label: 'Instruments', hint: 'market data', icon: Boxes, mnemonic: 'g i' },
  { href: '/exchanges', label: 'Exchanges', hint: 'market data', icon: Globe, mnemonic: 'g v' },
  { href: '/data-sources', label: 'Data Sources', hint: 'market data', icon: Database, mnemonic: 'g w' },
  { href: '/admin/observability', label: 'Observability', hint: 'admin', icon: HeartPulse, mnemonic: 'g o' },
];

const THEMES: ReadonlyArray<{ id: string; label: string; icon: LucideIcon }> = [
  { id: 'dark', label: 'Dark', icon: Moon },
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'hc', label: 'High contrast', icon: Palette },
  { id: 'amber', label: 'Amber floor', icon: Palette },
];

const DENSITIES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'compact', label: 'Compact' },
  { id: 'bloomberg', label: 'Bloomberg' },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const qc = useQueryClient();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const runAndClose = (fn: () => void) => {
    setOpen(false);
    setTimeout(fn, 0);
  };

  // Live entities pulled from the query cache (no extra fetches).
  const strategies = useMemo<ReadonlyArray<Strategy>>(() => {
    if (!open) { return []; }
    const cache = qc.getQueryCache().findAll({ queryKey: ['strategies'] });
    for (const entry of cache) {
      const data = entry.state.data as { data?: ReadonlyArray<Strategy> } | undefined;
      if (data?.data !== undefined) { return data.data; }
    }
    return [];
  }, [open, qc]);

  const clients = useMemo<ReadonlyArray<Client>>(() => {
    if (!open) { return []; }
    const cache = qc.getQueryCache().findAll({ queryKey: ['clients'] });
    for (const entry of cache) {
      const data = entry.state.data as { data?: ReadonlyArray<Client> } | undefined;
      if (data?.data !== undefined) { return data.data; }
    }
    return [];
  }, [open, qc]);

  const groups: NavGroup[] = [
    {
      heading: 'Navigate',
      rows: ROUTE_ROWS.map((r) => ({
        id: `route:${r.href}`,
        label: r.label,
        hint: r.hint,
        icon: r.icon,
        mnemonic: r.mnemonic,
        run: () => router.push(r.href as never),
      })),
    },
    ...(strategies.length > 0
      ? [
          {
            heading: `Strategies · ${strategies.length}`,
            rows: strategies.slice(0, 30).map((s) => ({
              id: `strategy:${s.id}`,
              label: s.code_name,
              hint: `${s.version} · ${s.status}`,
              icon: TrendingUp,
              run: () => router.push(`/strategies/${s.id}` as never),
            })),
          },
        ]
      : []),
    ...(clients.length > 0
      ? [
          {
            heading: `Clients · ${clients.length}`,
            rows: clients.slice(0, 30).map((c) => ({
              id: `client:${c.id}`,
              label: c.display_name,
              hint: `${c.tier} · ${c.status}`,
              icon: Users,
              run: () => router.push(`/clients/${c.id}` as never),
            })),
          },
        ]
      : []),
    {
      heading: 'Theme',
      rows: THEMES.map((t) => ({
        id: `theme:${t.id}`,
        label: `Switch to ${t.label}`,
        hint: 'theme',
        icon: t.icon,
        run: () => {
          document.documentElement.setAttribute('data-theme', t.id);
          try {
            localStorage.setItem('te:theme', t.id);
          } catch {
            /* ignored */
          }
        },
      })),
    },
    {
      heading: 'Density',
      rows: DENSITIES.map((d) => ({
        id: `density:${d.id}`,
        label: `Density · ${d.label}`,
        hint: 'compaction',
        icon: Rows3,
        run: () => {
          document.documentElement.setAttribute('data-density', d.id);
          try {
            localStorage.setItem('te:density', d.id);
          } catch {
            /* ignored */
          }
        },
      })),
    },
    {
      heading: 'Actions',
      rows: [
        {
          id: 'action:copy-url',
          label: 'Copy current URL',
          hint: 'share state',
          icon: Compass,
          run: () => {
            void navigator.clipboard?.writeText(window.location.href);
          },
        },
        {
          id: 'action:invalidate',
          label: 'Force-refresh all data',
          hint: 'invalidate query cache',
          icon: Activity,
          run: () => {
            void qc.invalidateQueries();
          },
        },
      ],
    },
  ];

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-bg/70 backdrop-blur-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-[18%] z-50 -translate-x-1/2',
            'w-full max-w-xl outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          )}
        >
          <DialogPrimitive.Title className="sr-only-true">Command palette</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only-true">
            Type to fuzzy-search across navigation, strategies, clients, and actions.
          </DialogPrimitive.Description>

          <Command
            label="Command palette"
            className={cn(
              'flex w-full flex-col overflow-hidden rounded-xl',
              'border border-border-strong bg-surface-elev shadow-popover',
            )}
            loop
          >
            <div className="flex items-center gap-2 border-b border-divider px-3.5 py-3">
              <Search className="h-4 w-4 shrink-0 stroke-[1.5] text-fg-subtle" aria-hidden />
              <Command.Input
                placeholder="Type a route, strategy, client or action…"
                className={cn(
                  'flex-1 bg-transparent text-sm text-fg outline-none',
                  'placeholder:text-fg-subtle',
                )}
              />
              <kbd className="font-mono text-2xs text-fg-subtle">esc</kbd>
            </div>

            <Command.List className="max-h-[60vh] overflow-y-auto px-1.5 py-2">
              <Command.Empty className="px-3 py-6 text-center text-sm text-fg-subtle">
                Nothing matches. Try a different term.
              </Command.Empty>

              {groups.map((g) => (
                <Command.Group
                  key={g.heading}
                  heading={
                    <span className="px-2 pb-1 pt-2 text-2xs font-medium text-fg-subtle">
                      {g.heading}
                    </span>
                  }
                >
                  {g.rows.map((row) => {
                    const Icon = row.icon;
                    return (
                      <Command.Item
                        key={row.id}
                        value={`${row.label} ${row.hint ?? ''} ${row.mnemonic ?? ''}`}
                        onSelect={() => runAndClose(row.run)}
                        className={cn(
                          'group flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-1.5',
                          'text-sm text-fg-muted',
                          'data-[selected=true]:bg-accent-soft data-[selected=true]:text-fg',
                          'transition-colors duration-fast',
                        )}
                      >
                        <Icon
                          className="h-3.5 w-3.5 shrink-0 stroke-[1.5] text-fg-subtle group-data-[selected=true]:text-accent"
                          aria-hidden
                        />
                        <span className="flex-1 truncate">{row.label}</span>
                        {row.hint !== undefined ? (
                          <span className="hidden text-2xs text-fg-subtle md:inline">
                            {row.hint}
                          </span>
                        ) : null}
                        {row.mnemonic !== undefined ? (
                          <kbd className="hidden md:inline">{row.mnemonic}</kbd>
                        ) : null}
                        <CornerDownLeft
                          className="h-3 w-3 stroke-[1.5] text-fg-subtle opacity-0 group-data-[selected=true]:opacity-100"
                          aria-hidden
                        />
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ))}
            </Command.List>

            <div className="flex items-center gap-3 border-t border-divider px-3 py-2 text-2xs text-fg-subtle">
              <span className="inline-flex items-center gap-1">
                <kbd>↑</kbd>
                <kbd>↓</kbd>
                navigate
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd>↵</kbd>
                run
              </span>
              <span className="inline-flex items-center gap-1">
                <kbd>esc</kbd>
                close
              </span>
              <span className="ml-auto">⌘K · trading-engine cockpit</span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
